/**
 * Solicitudes de colaborador («tejedores») — VIDA-307.
 *
 * Las tiendas permiten enviar una solicitud desde su pantalla de alta. Hasta
 * ahora entraban en la base y **nadie las veía**: no había ni listado ni forma
 * de responderlas. Esta es esa bandeja.
 *
 * Se controla con permisos propios (`weavers:read` y `weavers:manage`), no con
 * ser administrador de la organización: quien atiende las altas no tiene por
 * qué poder tocar la configuración ni los usuarios.
 *
 * Desde F2 de Tejedores, aprobar aquí **da de alta al colaborador**: crea su
 * organización de tejedor y su ficha de colaborador (pestaña «Colaboradores»).
 *
 * El cuerpo vive en `WeaverApplicationsInbox`, sin cabecera propia, para poder
 * embeberse como pestaña dentro de la página de Colaboradores. `onPendingChange`
 * sube el número de pendientes para pintar el contador de esa pestaña.
 */
import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { PageLoader } from '@/components/common';
import { weaversApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { PERMISSIONS } from '@/types';

type Estado = 'pending' | 'approved' | 'rejected';

interface Solicitud {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  website?: string;
  message?: string;
  status: Estado;
  review_notes?: string;
  reviewed_at?: string;
  // El modelo renombra los timestamps: created_at, no createdAt. Leer el nombre
  // equivocado daba «Invalid date» en todas las tarjetas.
  created_at: string;
}

const ETIQUETA: Record<Estado, string> = {
  pending: 'Pendiente',
  approved: 'Aprobada',
  rejected: 'Rechazada',
};

const COLOR: Record<Estado, string> = {
  // Pendiente en blanco/neutro: aun no se ha decidido nada. El verde y el rojo
  // solo aparecen una vez tomada la decision, para que salten a la vista.
  pending: 'bg-white border border-gray-300 text-gray-600',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

/**
 * Cuerpo de la bandeja, sin cabecera: se usa suelto (WeaversPage) y como
 * pestaña dentro de Colaboradores.
 */
export function WeaverApplicationsInbox({
  onPendingChange,
}: {
  onPendingChange?: (n: number) => void;
}) {
  const { hasPermission } = useAuthStore();
  const puedeResolver = hasPermission(PERMISSIONS.WEAVERS_MANAGE) || hasPermission('*');
  // Borrar es solo para super_admin: sirve para limpiar solicitudes de prueba.
  const puedeBorrar = hasPermission('*');

  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [filtro, setFiltro] = useState<'pending' | 'all'>('pending');
  const [cargando, setCargando] = useState(true);
  const [resolviendo, setResolviendo] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await weaversApi.listApplications({ status: filtro });
      setSolicitudes(res.data.data || []);
      onPendingChange?.(res.data.pending ?? 0);
    } catch {
      toast.error('No se pudieron cargar las solicitudes');
      setSolicitudes([]);
    } finally {
      setCargando(false);
    }
  }, [filtro, onPendingChange]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const borrar = async (id: string) => {
    if (!window.confirm('¿Eliminar esta solicitud? Se borra también la ficha que hubiera generado. No se puede deshacer.')) return;
    setResolviendo(id);
    try {
      await weaversApi.deleteApplication(id);
      toast.success('Solicitud eliminada');
      await cargar();
    } catch {
      toast.error('No se pudo eliminar la solicitud');
    } finally {
      setResolviendo(null);
    }
  };

  const resolver = async (id: string, status: 'approved' | 'rejected' | 'pending') => {
    setResolviendo(id);
    try {
      await weaversApi.resolveApplication(id, { status });
      const msg =
        status === 'approved' ? 'Colaborador dado de alta'
          : status === 'rejected' ? 'Solicitud rechazada'
          : 'Solicitud readmitida';
      toast.success(msg);
      await cargar();
    } catch {
      toast.error('No se pudo actualizar la solicitud');
    } finally {
      setResolviendo(null);
    }
  };

  if (cargando) return <PageLoader />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {(['pending', 'all'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFiltro(f)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
              filtro === f ? 'bg-vida-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f === 'pending' ? 'Pendientes' : 'Todas'}
          </button>
        ))}
      </div>

      {solicitudes.length === 0 ? (
        <div className="card p-12 text-center text-gray-500">
          {filtro === 'pending'
            ? 'No hay solicitudes pendientes.'
            : 'Todavía no ha llegado ninguna solicitud.'}
        </div>
      ) : (
        <div className="space-y-3">
          {solicitudes.map((s) => (
            <div key={s._id} className="card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{s.name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${COLOR[s.status]}`}>
                      {ETIQUETA[s.status]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    <a href={`mailto:${s.email}`} className="hover:underline">{s.email}</a>
                    {s.phone && <span className="text-gray-400"> · {s.phone}</span>}
                  </p>
                  {s.company && <p className="text-sm text-gray-600">{s.company}</p>}
                  {s.website && (
                    <a
                      href={s.website.startsWith('http') ? s.website : `https://${s.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-vida-600 hover:underline break-all"
                    >
                      {s.website}
                    </a>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 whitespace-nowrap">
                  <span className="text-xs text-gray-400">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString('es-ES') : ''}
                  </span>
                  {puedeBorrar && (
                    <button
                      onClick={() => borrar(s._id)}
                      disabled={resolviendo === s._id}
                      title="Eliminar solicitud (solo superadmin)"
                      className="text-xs text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                    >
                      Eliminar
                    </button>
                  )}
                </div>
              </div>

              {s.message && (
                <p className="mt-3 text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-line">
                  {s.message}
                </p>
              )}

              {s.status === 'pending' && puedeResolver && (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => resolver(s._id, 'approved')}
                    disabled={resolviendo === s._id}
                    className="px-3 py-1.5 rounded-lg text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                  >
                    Aprobar y dar de alta
                  </button>
                  <button
                    onClick={() => resolver(s._id, 'rejected')}
                    disabled={resolviendo === s._id}
                    className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Rechazar
                  </button>
                </div>
              )}
              {s.status === 'pending' && !puedeResolver && (
                <p className="mt-3 text-xs text-gray-400 italic">
                  No tienes permiso para resolver solicitudes.
                </p>
              )}

              {/* Una solicitud ya resuelta se puede devolver a pendiente: sirve
                  para deshacer un rechazo (o una aprobacion) hecho por error. */}
              {s.status !== 'pending' && puedeResolver && (
                <div className="mt-4">
                  <button
                    onClick={() => resolver(s._id, 'pending')}
                    disabled={resolviendo === s._id}
                    className="px-3 py-1.5 rounded-lg text-sm border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {s.status === 'rejected' ? 'Readmitir' : 'Volver a pendiente'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500">
        Aprobar <strong>da de alta al colaborador</strong>: crea su organización de tejedor y su
        ficha en la pestaña «Colaboradores». Rechazar o readmitir se puede deshacer.
      </p>
    </div>
  );
}

/** Página suelta (por compatibilidad); la ruta usa ahora la de Colaboradores. */
export default function WeaversPage() {
  return (
    <div>
      <Header title="Solicitudes de colaborador" subtitle="Altas llegadas desde las tiendas" />
      <div className="p-6">
        <WeaverApplicationsInbox />
      </div>
    </div>
  );
}
